import { registerPlugin } from "@capacitor/core";

export interface LocationPoint {
  lat: number;
  lng: number;
  timestamp: number;
  speed: number | null;
  accuracy: number | null;
}

export interface TrackingState {
  isTracking: boolean;
  sessionId: string | null;
  totalMiles: number;
  lastLat: number | null;
  lastLng: number | null;
  lastUpdate: number | null;
  isStationary: boolean;
  isLowPowerMode: boolean;
}

export interface StartTrackingOptions {
  sessionId: string;
  initialLat?: number;
  initialLng?: number;
}

export interface StartTrackingResult {
  success: boolean;
}

export interface StopTrackingResult {
  sessionId: string;
  totalMiles: number;
  pointCount: number;
  duration: number;
}

export interface ResumeSessionOptions {
  sessionId: string;
}

export interface ResumeSessionResult {
  success: boolean;
  restoredMiles: number;
}

export type LocationPermissionStatus =
  | "notDetermined"
  | "restricted"
  | "denied"
  | "authorizedAlways"
  | "authorizedWhenInUse"
  | "unknown";

export interface PermissionStatusResult {
  status: number;
  statusName: LocationPermissionStatus;
}

export interface GetLocationPointsResult {
  points: LocationPoint[];
}

export interface NativeLocationPlugin {
  /**
   * Start tracking a new session.
   * Creates a new session in the native database and begins GPS tracking.
   */
  startTracking(options: StartTrackingOptions): Promise<StartTrackingResult>;

  /**
   * Stop tracking and get final session data.
   * Ends the current session and returns accumulated metrics.
   */
  stopTracking(): Promise<StopTrackingResult>;

  /**
   * Resume an existing session from the native database.
   * Use when app was terminated by iOS and relaunched.
   * Restores accumulated miles and restarts GPS tracking.
   */
  resumeSession(options: ResumeSessionOptions): Promise<ResumeSessionResult>;

  /**
   * Get the current tracking state.
   * Returns tracking status, accumulated miles, and last known position.
   */
  getTrackingState(): Promise<TrackingState>;

  /**
   * Get all location points for a session.
   * Useful for route visualization.
   */
  getLocationPoints(options: {
    sessionId: string;
  }): Promise<GetLocationPointsResult>;

  /**
   * Clear all data for a session.
   * Call after syncing data to server to free up storage.
   */
  clearSession(options: { sessionId: string }): Promise<void>;

  /**
   * Get current location permission status.
   * Returns the iOS authorization level.
   */
  getPermissionStatus(): Promise<PermissionStatusResult>;

  /**
   * Request location permission.
   * Triggers the iOS permission dialog if not yet determined.
   */
  requestPermission(): Promise<PermissionStatusResult>;
}

const NativeLocation = registerPlugin<NativeLocationPlugin>("NativeLocation", {
  web: () =>
    import("./native-location-web").then((m) => new m.NativeLocationWeb()),
});

export default NativeLocation;
